import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import CloseButton from '../../common/CloseButton/CloseButton';
import InputBoxLarge from '../../common/InputBox/InputBoxLarge';
import InputBoxSmall from '../../common/InputBox/InputBoxSmall';
import SelectBoxLarge from '../../common/SelectBox/SelectBoxLarge';
import SelectBoxSmall from '../../common/SelectBox/SelectBoxSmall';
import '../../common/CommonText.css';
import TextBox from '../../common/TextBox/TextBox';
import UploadBox from '../../common/InputBox/UploadBox';
import InputAndSelectCompoBoxSmall from '../../common/InputBox/InputAndSelectCompo';
import UploadBoxCountry from '../../common/InputBox/UploadCountry';
import InputAndSelectWithIconBoxSmall from '../../common/InputBox/InpuSelectIcon';
import SearchSelectBox from '../../common/SelectBox/SelectBoxWithSearch';
import FeaturesList from '../../common/FeaturesListAdd/FeaturesListAdd';
import EditUploadBoxCountry from '../../common/InputBox/EditUpload';
import RichTextBox from '../../common/InputBox/RichTextBox';
import EditCategoryUploadBox from '../../common/InputBox/EditCategoryUpload';
import ToggleSwitch from '../../common/ToggleButton/ToggleSwitch';
import SearchSelectBoxDrown from '../../common/SelectBox/searchSelect';
import SearchSelectTimezone from '../../common/SelectBox/SelectBoxWithSearchTimezone';

const EditPopup = ({ isOpen, title, buttonName = "Save", fields, onSave, onClose, popupErrorMessage }) => {


  const [formValues, setFormValues] = useState(() => {
    const initial = fields.reduce((acc, field) => {
      if (field.name) {
        acc[field.name] = field.defaultValue ?? '';
      }
      if (field.opt) {
        acc[field.opt] = field.defaultOption ?? '';
      }
      return acc;
    }, {});
    return initial;
  });
  
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  
  useEffect(() => {
    setFormValues(
      fields.reduce((acc, field) => {
        if (field.name) {
          acc[field.name] = field.defaultValue ?? '';
        }
        if (field.opt) {
          acc[field.opt] = field.defaultOption ?? '';
        }
        return acc;
      }, {})
    );
  
    // Clear errors when fields change
    setFormErrors({});
  }, [fields]);
  


  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    setFormValues(prev => ({
      ...prev,
      [field]: value,
    }));
    

    // Clear error when field is modified
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleSelectChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    setFormValues(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when field is modified
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const validateField = (field) => {
    const { name, validators = [] } = field;
  
    // Skip validation if field is not present in formData
    if (!(name in formData)) return null;
  
    const value = formData[name];
  
    for (const validator of validators) {
      const result = validator(value);
      if (typeof result === 'string') {
        return result; // First error
      }
    }
  
    return null;
  };
  
  

  const handleSubmit = () => {
    const errors = {};
    let hasErrors = false;
  
    fields.forEach((field) => {
      if (!field.validators) return;
  
      const errorMessage = validateField(field);
      if (errorMessage) {
        errors[field.name] = errorMessage;
        hasErrors = true;
      }
    });
  
    setFormErrors(errors);
  
    if (hasErrors) return;
  
    onSave(formData);
    setFormData({});
  };
  
  


  const handleClose = () => {

    setFormErrors({});
    setFormData({});
    if (onClose) onClose();
  };

  // Helper function to render error message
  const renderErrorMessage = (fieldName) => {
    if (!formErrors[fieldName]) return null;
    
    return (
      <div className="validation-error" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
        {formErrors[fieldName]}
      </div>
    );
  };

  const renderField = (field, index) => {
    const commonProps = {
      key: index,
      ...field,
      value: formValues[field.name],
      error: formErrors[field.name],
      border:'blue-border'
    };
    
    switch (field.type) {
      case 'small-input':
        return (
          <div key={index} className="field-container w-50">
            <InputBoxSmall
              {...commonProps}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
            />
          </div>
        );
      case 'large-input':
        return (
          <div key={index} className="field-container w-100">
            <InputBoxLarge
              {...commonProps}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
            />
          </div>
        );

      case 'large-select':
        return (
          <div key={index} className="field-container w-100">
            <SearchSelectBoxDrown
              {...commonProps}
              field={field}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
            />
          </div>
        );
      case 'small-select':
        return (
          <div key={index} className="field-container w-50">
            <SearchSelectBoxDrown
              {...commonProps}
              field={field}
              className='w-48'
              onChange={(e) => handleInputChange(field.name, e.target.value)}
            />
          </div>
        );
      case 'upload-box':
        return (
          <div key={index} className="field-container w-100">
            <UploadBox 
              {...commonProps}
              onFileChange={(file) => handleInputChange(field.name, file)}
            />
          </div>
        );
      case 'upload-box-country':
        return (
          <div key={index} className="field-container w-100">
            <UploadBoxCountry 
              {...commonProps}
              onFileChange={(file) => handleInputChange(field.name, file)}
            />
          </div>
        );
      case 'edit-upload-box-country':
        return (
          <div key={index} className="field-container w-100">
            <EditUploadBoxCountry 
              {...commonProps}
              onFileChange={(file) => handleInputChange(field.name, file)}
            />
          </div>
        );
        case 'edit-upload-category':
          return (
            <div key={index} className="field-container w-100">
              <EditCategoryUploadBox 
                {...commonProps}
                onFileChange={(file) => handleInputChange(field.name, file)}
              />
            </div>
          );
      case 'custom':
        return (
          <div key={index} className="field-container custom-field-container">
            {field.component({
              ...commonProps,
              onChange: (value) => handleInputChange(field.name, value)
            })}
            {renderErrorMessage(field.name)}
          </div>
        );
      case 'input-select-combo-small':
        return (
          <div key={index} className="field-container w-50">
            <InputAndSelectCompoBoxSmall
              {...commonProps}
              label={field.label}
              options={field.options}
              value={formValues[field.name]}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              selectchange={(e) => handleInputChange(field.select, e.target.value)} 
            />
          </div>
        );
      case 'features-list':
        return (
          <div key={index} className="field-container w-100">
            <FeaturesList
              {...commonProps}
              title={field.label}
              allFeatures={field.allFeatures || []}
              defaultSelectedOptions={formValues[field.name] || []}
              onChange={(selectedFeatures) => {
                handleInputChange(field.name, selectedFeatures);
              }}
            />
          </div>
        );
      case 'input-select-icon':
        return (
          <div key={index} className="field-container w-50">
            <InputAndSelectWithIconBoxSmall
              {...commonProps}
              value={formValues[field.name]} 
              defaultOption={formValues[field.opt] || ''}
              onChange={({ type, value }) => {
                if (type === 'input') {
                  handleInputChange(field.name, value); 
                } else if (type === 'select') {
                  handleSelectChange(field.opt, value); 
                }
              }}
            />
          </div>
        );
      case 'text':
        return (
          <div key={index} className="field-container w-100">
            <TextBox 
              {...commonProps}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
            />
          </div>
        );
      case 'rich-text':
        return (
          <div key={index} className="field-container w-100">
            <RichTextBox
              {...commonProps}
              onChange={(value) => handleInputChange(field.name, value)}
            />
            {renderErrorMessage(field.name)}
          </div>
        );
      case 'small-date':
      case 'small-time':
        return (
          <div key={index} className="field-container w-50">
            <InputBoxSmall
              {...commonProps}
              type={field.type}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
            />
          </div>
        );
      case 'date':
      case 'time':
        return (
          <div key={index} className="field-container w-100">
            <InputBoxLarge
              {...commonProps}
              type={field.type}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
            />
          </div>
        );
      case 'toggle':
        return (
          <div key={index} className="toggle-container-pop w-100">
              <span className='fts-12 ftw-4' style={{color:"#6D6D6D",marginLeft:"5px"}}>Apply To Company</span>
            <ToggleSwitch 
              onToggle={(value) => handleInputChange(field.name, value)}
              defaultValue={field?.defaultValue}
            />
          </div>
        );
      case 'search-select': 
        return (
          <div key={index} className="field-container w-100">
            <SearchSelectBox 
              {...commonProps}
              allPlans={field.options || []}
              defaultSelectedOptions={formValues[field.name] || field.defaultValue || []}
              onChange={(selectedOptions) => {
                handleInputChange(field.name, selectedOptions);
              }}
            />
          </div>
        );
      case 'search-select-timezone': 
        return (
          <div key={index} className="field-container w-100">
            <SearchSelectTimezone
              {...commonProps}
              allPlans={field.options || []}
              defaultSelectedOptions={formValues[field.name] || field.defaultValue || []}
              onChange={(selectedOptions) => {
                handleInputChange(field.name, selectedOptions);
              }}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="popup-overlay">
      <div className="popup popup1 ">
        <CloseButton onClick={handleClose} />
        {popupErrorMessage && (
          <div className="popup-error-message fts-13" style={{ color: 'red', marginBottom: '10px' }}>
            {popupErrorMessage}
          </div>
        )}
        <div className="popup-name-button">
          <h2 className="popup-name fts-17 common-text ftw-5 black">{title}</h2>
          <button className="popup-add-button white fts-15 ftw-5" onClick={handleSubmit}>{buttonName || "Save"}</button>
        </div>
        <div className="add-input-box-container ai-center edit-popup">
          {fields.map((field, index) => renderField(field, index))}
        </div>
      </div>
    </div>
  );
};

EditPopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  buttonName: PropTypes.string,
  fields: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  popupErrorMessage: PropTypes.string
};

export default EditPopup;